import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import BreakGlassDialog from 'components/BreakGlass/BreakGlassDialog';
import { getLoggedInUserDetails, sendBreakGlassLog } from 'utils/breakGlass';

import { SettingsOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  doordashMapProvider,
  grubHubMapProvider,
} from 'redux/actions/Menu/menuV2';
import {
  SET_LINK_PROVIDER_VISIBILITY,
  SET_LINK_PROVIDER,
} from 'redux/actions/types';
import { PROVIDER_CONNECTED } from 'constants/providerStatuses';
import Private from 'components/Private';
import CircularProgress from '@mui/material/CircularProgress';
import { isPermissionWithCompanyType } from 'hooks/useCompanyType';
import { useTranslation } from 'react-i18next';
import useProviderConfig from 'hooks/useProviderConfig';
import { isWebsiteConnected } from 'utils/checkAccessDenied';
import { isSuperRole } from 'utils/roles';
import UpdateProviderMenuButton from './UpdateProviderMenuButton';

const CustomizedSettings = styled(SettingsOutlined)`
  :hover {
    color: #fff;
  }
`;
const CustomizedButton = styled(Button)`
  :hover svg {
    color: #fff;
  }
`;

const BREAK_GLASS_PAGE = 'menu-doordash-remap';

const MasterMenuHeader = ({ handleDrawerOpen, open }) => {
  const dispatch = useDispatch();
  const [isBulked, setIsBulked] = useState(false);
  const { providers } = useProviderConfig();
  const {
    menu,
    formProduct,
    isDoordashProviderLoading,
    isDoordashProviderBtnHidden,
  } = useSelector(({ menu }) => menu);
  const { items, clicked } = useSelector(({ providers }) => providers);
  const { currentBusiness } = useSelector(({ business }) => business);
  const loggedInUser = useSelector(({ user }) => user.data);
  const catProducts = [];
  const categoriesBulk = [];
  const modifiersGroup = [];
  const menuBulk = useMemo(
    () => menu?.bulkInfo?.isSaved,
    [menu, menu?.bulkInfo?.isSaved]
  );
  const { isTypeSingle } = isPermissionWithCompanyType();
  const [showButton, setShowButton] = useState(false);
  const [isDoordashProviderLoadingLocal, setIsDoordashProviderLoadingLocal] =
    useState(false);
  const [remapConfirmOpen, setRemapConfirmOpen] = useState(false);
  const [remapBreakGlassOpen, setRemapBreakGlassOpen] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    if (menu?.categories?.length) {
      menu.categories.forEach((item) => {
        categoriesBulk.push(item);
        if (item?.products?.length) {
          item.products.forEach((product) => {
            catProducts.push(product);
          });
        }
      });
    }
    if (menu?.modifiersAll?.length) {
      menu.modifiersAll.forEach((item) => {
        modifiersGroup.push(item);
      });
    }

    const checkSaveProducts = catProducts.every(
      (item) => item.bulkInfo?.isSaved
    );
    const checkSaveModifiers = modifiersGroup.every(
      (item) => item.bulkInfo?.isSaved
    );
    const checkSaveCategories = categoriesBulk.every(
      (item) => item.bulkInfo?.isSaved
    );

    setIsBulked(
      !checkSaveProducts ||
        !checkSaveCategories ||
        !checkSaveModifiers ||
        !menuBulk
    );
  }, [menu?.categories, isBulked, catProducts, categoriesBulk, modifiersGroup]);

  useEffect(() => {
    dispatch({ type: 'MENU_IS_BULKED', payload: isBulked });
  }, [isBulked]);

  useEffect(() => {
    if (
      (menu?.connectedProviders && menu?.connectedProviders?.doordash) ||
      isDoordashProviderBtnHidden
    ) {
      setShowButton(false);
    } else if (
      menu?.connectedProviders &&
      !menu?.connectedProviders?.doordash
    ) {
      const doordashStatus = items?.find(
        (item) => item.srv === providers.doordash.srv
      );
      if (!clicked && doordashStatus?.state && doordashStatus?.status === 2) {
        setShowButton(true);
      }
    }
  }, [
    menu?.connectedProviders,
    items,
    showButton,
    clicked,
    isDoordashProviderBtnHidden,
  ]);

  useEffect(() => {
    if (!formProduct.bulkInfo?.isSaved) {
      setIsBulked(!formProduct.bulkInfo?.isSaved);
    }
  }, [formProduct.bulkInfo?.isSaved]);

  const targetConnectedProviders = useMemo(() => {
    const isWebsite = isWebsiteConnected(items);
    const res = { website: isWebsite };
    items.forEach((providerItem) => {
      res[providerItem.srv] = providerItem.status == PROVIDER_CONNECTED;
    });
    return res;
  }, [items]);

  const getConnectedDefaultProvider = () => {
    dispatch({ type: SET_LINK_PROVIDER_VISIBILITY, payload: true });
    dispatch({
      type: SET_LINK_PROVIDER,
      payload: {
        connectedProviders: menu?.connectedProviders,
        targetConnectedProviders: targetConnectedProviders,
        target: 'menu',
      },
    });
  };

  const handleMenuLinkProvider = () => {
    getConnectedDefaultProvider();
  };

  const checkCurrentBusiness = useMemo(() => {
    return isSuperRole(currentBusiness?.roles);
  }, [currentBusiness]);

  const isMenuLinkedToDoordash = useMemo(() => {
    return Boolean(menu?.connectedProviders?.doordash);
  }, [menu?.connectedProviders]);

  const handleProviderMap = (provider) => {
    if (provider === providers.doordash.srv) {
      let mapBodyDoorDash = {
        provider: provider,
        action: 'map',
        isLinked: true,
      };
      setShowButton(false);
      const mapPromise = dispatch(doordashMapProvider(mapBodyDoorDash));
      setTimeout(() => {
        dispatch({ type: 'PROVIDERS_DOORDASH_CLICK', payload: true });
      }, 500);

      setTimeout(() => {
        setIsDoordashProviderLoadingLocal(false);
      }, 2000);
      return mapPromise;
    } else if (provider === providers.grubhub.srv) {
      let mapBodyGrubHub = {
        provider: provider,
        action: 'map',
        isLinked: true,
      };
      dispatch(grubHubMapProvider(mapBodyGrubHub));
    }
  };

  const remapActionSummary = `Remap ${providers?.doordash?.label} menu`;

  const handleRemapClick = () => {
    if (isDoordashProviderLoading || isDoordashProviderLoadingLocal) {
      return;
    }
    setRemapConfirmOpen(true);
  };

  const handleCloseRemapConfirm = () => {
    setRemapConfirmOpen(false);
  };

  const handleConfirmRemap = () => {
    setRemapConfirmOpen(false);
    setRemapBreakGlassOpen(true);
  };

  const handleRemapAuthorized = (employeeName) => {
    const loggedInUserDetails = getLoggedInUserDetails(loggedInUser);
    setIsDoordashProviderLoadingLocal(true);
    Promise.resolve(handleProviderMap(providers?.doordash?.srv))
      .then(() => {
        sendBreakGlassLog(
          {
            eventType: 'menu-doordash-remap',
            employeeName,
            actionSummary: remapActionSummary,
            result: 'success',
            loggedInUser: loggedInUserDetails,
          },
          BREAK_GLASS_PAGE
        );
      })
      .catch(() => {
        sendBreakGlassLog(
          {
            eventType: 'menu-doordash-remap',
            employeeName,
            actionSummary: remapActionSummary,
            result: 'failed',
            loggedInUser: loggedInUserDetails,
          },
          BREAK_GLASS_PAGE
        );
      });
  };
  return (
    <Box sx={{ pt: '8px' }}>
      <Box
        sx={{
          flexWrap: 'wrap',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            id="masterMenuHeaderMenu"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography sx={{ color: '#3D4350', fontSize: '20px' }}>
            {t('Menu')}
          </Typography>
          <UpdateProviderMenuButton buttonId="MasterMenuSync" />
          {isTypeSingle ? (
            <Private permission="MENU_EDIT" disabled={true}>
              <CustomizedButton
                id="masterMenuHeaderProvider"
                onClick={handleMenuLinkProvider}
                sx={{
                  ml: 2,
                  background: !isBulked ? 'rgba(0, 0, 0, 0.12)' : '#E5E5E5',
                  minWidth: '40px',
                  width: '1em',
                  height: '2.62em',
                }}
                variant="contained"
                // disabled={!linked}
              >
                <CustomizedSettings
                  sx={{ color: '#323232', fontSize: '20px' }}
                />
              </CustomizedButton>
            </Private>
          ) : (
            <></>
          )}
          {checkCurrentBusiness && (
            <>
              {showButton && (
                <Button
                  onClick={() => {
                    setIsDoordashProviderLoadingLocal(true);
                    isDoordashProviderLoadingLocal || isDoordashProviderLoading
                      ? () => {}
                      : handleProviderMap(providers?.doordash?.srv);
                  }}
                  id="providerMap"
                  sx={{ ml: 2 }}
                  variant="contained"
                  disabled={
                    isDoordashProviderLoading || isDoordashProviderLoadingLocal
                  }
                  startIcon={
                    (isDoordashProviderLoading ||
                      isDoordashProviderLoadingLocal) && (
                      <CircularProgress size={14} />
                    )
                  }
                >
                  {`${providers.doordash.label} map`}
                </Button>
              )}
              {isMenuLinkedToDoordash && (
                <Button
                  onClick={handleRemapClick}
                  id="providerRemap"
                  sx={{ ml: 2 }}
                  variant="contained"
                  disabled={
                    isDoordashProviderLoading || isDoordashProviderLoadingLocal
                  }
                  startIcon={
                    (isDoordashProviderLoading ||
                      isDoordashProviderLoadingLocal) && (
                      <CircularProgress size={14} />
                    )
                  }
                >
                  {`${providers.doordash.label} remap`}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
      <Dialog
        open={remapConfirmOpen}
        onClose={handleCloseRemapConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{`Confirm ${providers?.doordash?.label} Remap`}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {remapActionSummary}
          </DialogContentText>
          <DialogContentText>
            We will record this action and send the information to management.
            Are you sure you want to do that?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemapConfirm}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmRemap}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <BreakGlassDialog
        open={remapBreakGlassOpen}
        onClose={() => setRemapBreakGlassOpen(false)}
        onAuthorized={handleRemapAuthorized}
        page={BREAK_GLASS_PAGE}
        actionSummary={remapActionSummary}
        description="Enter the employee name and break-glass code to authorize the DoorDash remap."
      />
    </Box>
  );
};

export default MasterMenuHeader;
